// El teclado de los `radiogroup` del menú del "+" —
// ADR-2026-09-14-los-radiogroup-del-menu-se-manejan-con-el-teclado.
//
// Módulo aparte y puro (salvo el `focus`, que es el punto) para poder probar la cuenta de
// índices: es la clase de código que "anda" hasta que alguien recorre las opciones con el
// teclado y descubre que la última no vuelve a la primera.

import type { KeyboardEvent } from 'react';

/**
 * Las props de teclado de UN radio adentro de un `role="radiogroup"`
 * (ADR-2026-09-14-los-radiogroup-del-menu-se-manejan-con-el-teclado).
 *
 * Dos cosas que un radiogroup necesita y que un puñado de `<button>` no trae:
 *
 *  • **un solo tab stop**: el elegido (o el primero si no hay ninguno). Con seis botones
 *    tabbables, Tab recorría las seis opciones como si fueran controles sueltos;
 *  • **flechas que mueven Y eligen**, que es como manda ARIA y como funciona un grupo de
 *    radios nativo. Home/End van a los extremos.
 *
 * El grupo se recorre por el DOM (`closest('[role="radiogroup"]')`) y no por refs: esto se
 * llama adentro de un `.map`, así que no puede ser un hook, y el orden del DOM es el mismo
 * de `ids` porque salen del mismo array.
 */
export function propsDeRadio<V>(
  ids: readonly V[],
  valor: V,
  propio: V,
  elegir: (destino: V) => void,
): { tabIndex: number; onKeyDown: (ev: KeyboardEvent<HTMLButtonElement>) => void } {
  const conFoco = ids.includes(valor) ? valor : ids[0];
  return {
    tabIndex: propio === conFoco ? 0 : -1,
    onKeyDown: (ev) => {
      const paso =
        ev.key === 'ArrowDown' || ev.key === 'ArrowRight'
          ? 1
          : ev.key === 'ArrowUp' || ev.key === 'ArrowLeft'
            ? -1
            : 0;
      let destino: number;
      if (paso !== 0) {
        destino = (ids.indexOf(propio) + paso + ids.length) % ids.length;
      } else if (ev.key === 'Home') {
        destino = 0;
      } else if (ev.key === 'End') {
        destino = ids.length - 1;
      } else {
        return;
      }
      ev.preventDefault();
      // El menú también escucha flechas (el foco rotativo de la lista): acá mandan las
      // del grupo.
      ev.stopPropagation();
      const grupo = ev.currentTarget.closest('[role="radiogroup"]');
      const botones = grupo
        ? Array.from(grupo.querySelectorAll<HTMLButtonElement>('[role="radio"]'))
        : [];
      elegir(ids[destino]!);
      botones[destino]?.focus();
    },
  };
}
